import { BlobCreate } from "@pdfking/compatibility"
import type { Bytes } from "@pdfking/tfunc"
import {
  ResizeUInt8,
  ResponseContentLength,
  SafePromise,
  StreamToIterable,
} from "@pdfking/tfunc"

import { EngineError } from "../../errors/EngineError"
import { GenerateFile } from "../../files/GenerateFile"
import { ProgressFetch } from "../../network/ProgressFetch"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import { ErrorName } from "../../types/errors/ErrorName"
import type { RemoteFileChunk } from "../../types/files/cached/RemoteFileChunk"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskDownloadOptions } from "../../types/tasks/options/TaskDownloadOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

export async function* TaskDownload(
  engine: PdfEngine,
  task: TaskDownloadOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const {
    source: remote,
    source: { mirrors },
    options: { chunk_size },
  } = task

  for (const url of mirrors) {
    const { value: response } = await SafePromise(
      ProgressFetch(url, {
        download: e =>
          emit({
            name: TrackedTaskName.DOWNLOAD,
            ...e,
          }),
      })
    )

    if (response && response.ok) {
      const stream = ResizeUInt8(StreamToIterable(response.body), chunk_size)
      const bytes_total = ResponseContentLength(response)

      let bytes_start = 0 as Bytes

      for await (const chunk of stream) {
        const bytes_end = (bytes_start + chunk.length) as Bytes

        yield {
          enum: TaskName.CONTENT_FILE,
          name: `chunk_${bytes_start}.bin`,
          content: {
            enum: VirtualFileType.REMOTE_FILE_CHUNK,
            remote,
            bytes_start,
            bytes_end,
            bytes_total,
            file_meta: remote.file_meta,
            content: await GenerateFile(
              engine,
              [BlobCreate(chunk)],
              ContentType.BYTES,
              emit
            ),
          } as RemoteFileChunk,
        }

        bytes_start = bytes_end
      }

      return
    }
  }

  throw EngineError(ErrorName.FILE_NOT_FOUND)
}
